import { OpenTabDetailFragment } from '@/features/graphql/generated/graphql'
import { logger } from '@/features/openTabs/logger'
import { OpenTabDetailDocument } from '@/features/openTabs/queries'
import { useHandleErrorWithToast } from '@/hooks/useHandleErrorWithToast'
import { useApolloClient } from '@apollo/client'
import { useCallback, useEffect, useRef, useState } from 'react'

type Selection = { uuid: string | null | undefined; revision: number; attempt: number }
type DetailState = Selection & { tab?: OpenTabDetailFragment; loading: boolean; failed: boolean }
const sameSelection = (left: Selection, right: Selection) =>
    left.uuid === right.uuid && left.revision === right.revision && left.attempt === right.attempt

export const useOpenTabDetail = (uuid: string | null | undefined, revision = 0) => {
    const client = useApolloClient()
    const handleError = useHandleErrorWithToast(logger)
    const [attempt, setAttempt] = useState(0)
    const latest = useRef<Selection>({ uuid, revision, attempt })
    latest.current = { uuid, revision, attempt }
    const [state, setState] = useState<DetailState>({
        ...latest.current,
        loading: !!uuid,
        failed: false,
    })
    const runRef = useRef<() => Promise<OpenTabDetailFragment | undefined>>(async () => undefined)

    useEffect(() => {
        let active = true
        let request: Promise<OpenTabDetailFragment | undefined> | undefined
        const run = (): Promise<OpenTabDetailFragment | undefined> => {
            if (request) return request
            if (!uuid) return Promise.resolve(undefined)
            request = (async () => {
                while (active && latest.current.uuid === uuid) {
                    const selection = latest.current
                    setState((previous) => ({
                        ...selection,
                        tab: previous.uuid === uuid ? previous.tab : undefined,
                        loading: true,
                        failed: false,
                    }))
                    try {
                        const { data } = await client.query({
                            query: OpenTabDetailDocument,
                            variables: { uuid },
                            fetchPolicy: 'no-cache',
                            context: { queryDeduplication: false },
                        })
                        if (!active) return undefined
                        if (!sameSelection(selection, latest.current)) continue
                        if (!data.openTab) throw new Error('Open tab detail is not available')
                        setState({ ...selection, tab: data.openTab, loading: false, failed: false })
                        return data.openTab
                    } catch (error) {
                        if (!active) return undefined
                        if (!sameSelection(selection, latest.current)) continue
                        setState((previous) => ({
                            ...selection,
                            tab: previous.uuid === uuid ? previous.tab : undefined,
                            loading: false,
                            failed: true,
                        }))
                        void handleError(error)
                        throw error
                    }
                }
                return undefined
            })().finally(() => {
                request = undefined
            })
            return request
        }
        runRef.current = run
        void run().catch(() => {})
        return () => {
            active = false
            runRef.current = async () => undefined
        }
    }, [client, handleError, uuid])

    useEffect(() => {
        void runRef.current().catch(() => {})
    }, [revision])

    const refetch = useCallback(() => {
        latest.current = { ...latest.current, attempt: latest.current.attempt + 1 }
        setAttempt(latest.current.attempt)
        return runRef.current()
    }, [])
    const retry = useCallback(() => {
        void refetch().catch(() => {})
    }, [refetch])
    const current = sameSelection(state, latest.current)

    return {
        tab: state.uuid === uuid ? state.tab : undefined,
        loading: !!uuid && (!current || state.loading),
        failed: !!uuid && current && state.failed,
        retry,
        refetch,
    }
}
